import { useCallback, useEffect, useMemo } from 'react';

import RoomDetailCard from '@view/components/Cards/RoomDetail';
import AccommodationModel from '@data/models/Accommodation';
import { withStore } from '@data/other/withStore';
import { observer } from 'mobx-react';

import Calendar from '@components/Calendar';
import { Box, Flex, Stack } from '@chakra-ui/react';
import Text from '@molecules/Text';
import styled from '@emotion/styled';
import BEIcon from '@molecules/BEIcon';
import Card from '@molecules/Card';
import { StoreProps } from '@data/stores/Store';
import SkeletonRoomDetail from '@view/components/Cards/RoomDetail/SkeletonRoomDetail';

const StyledNotification = styled(Box)`
  display: flex;
  padding: 16px;
  border: 1px solid #F3565D;
  border-radius: 10px;
  background-color: #FEEEEF;
  gap: 8px;
`;

const Accommodation = ({ store: { property } }: StoreProps) => {
  const {
    accommodations: { model: accommodations },
  } = property;

  const packedAccommos: AccommodationModel[][] | [] = useMemo(() => {
    const newAccommos = {};
    accommodations?.items.map((acc) => {
      if (!newAccommos[acc.id]) {
        newAccommos[acc.id] = [];
      }
      newAccommos[acc.id].push(acc);
    });
    return Object.values(newAccommos);
  }, [accommodations?.items]);

  const renderRoomList = useCallback(() => {
    return (
      <Flex direction="column" gap={8}>
        {packedAccommos.map(
          (accommodations: AccommodationModel[], index: number) => (
            <Box key={index}>
              <RoomDetailCard
                accommodation={accommodations[0]}
                packages={accommodations}
              />
            </Box>
          )
        )}
      </Flex>
    );
  }, [packedAccommos]);

  const renderCalendar = useCallback(() => {
    return (
      <Stack gap="16px">
        <StyledNotification>
          <BEIcon name="warn-circle" />
          <Text>
            There&apos;s no availability for the selected dates.
            <br />
            Select different dates or contact the property at{' '}
            {property.model?.contact.phone}
          </Text>
        </StyledNotification>
        <Card>
          <Calendar searchOnSelect={true} />
        </Card>
      </Stack>
    );
  }, [property.model]);

  const renderLoader = useCallback(() => {
    // eslint-disable-next-line react/jsx-key
    return (
      <Flex direction="column" gap={8}>
        {[...Array(3)].map((v, index) => (
          <SkeletonRoomDetail key={index} />
        ))}
      </Flex>
    );
  }, []);

  if (!accommodations) {
    return renderLoader();
  }

  return (
    <>
      {(accommodations?.items.length || 0) > 0
        ? renderRoomList()
        : renderCalendar()}
    </>
  );
};

export default withStore(observer(Accommodation));
